import { isNil } from 'lodash'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Layout,
  Modal,
  Spin,
  message,
  Button,
} from '@surecompanies/core_components'
import {
  BarChartOutlined,
  CheckSquareOutlined,
  UserAddOutlined,
} from '@ant-design/icons'
import { browserHistory } from 'react-router'
import Page from 'layouts/Page'
import PageWrapper from 'enrollment-platform/PageWrapper'
import BreadcrumbNav from 'enrollment-platform/components/Breadcrumbs'
import { FINANCE_PATH } from '../../constants'
import { useSelector } from 'react-redux'
import { selectors as authSelectors } from '@hixme/auth-ui'
import { ROLE_PLATFORM_HIXME_ADMIN } from '@hixme/role-policy'
import { PaymentAssignmentRole } from '../../components/PaymentAssignment/Utils/roles'
import { usePaymentAssignmentRolePolicy } from '../../components/PaymentAssignment/Utils/rolePolicy'
import {
  PaymentCase,
  Priority,
  PaymentStatus,
  PaymentAssignmentStatusId,
  TerminatedFilter,
  TestUserFilter,
} from '../../components/PaymentAssignment/Utils/types'
import {
  useAssignPaymentAssignmentsMutation,
  useCreatePaymentBotTaskMutation,
  useBulkCreatePaymentBotTaskMutation,
  useBulkCancelPaymentBotTaskMutation,
  useCancelPaymentBotTaskMutation,
  usePaymentAssignmentUserRoleQuery,
  usePaymentAssignmentTaskSummaryQuery,
} from '../../api/finance-api-client'
import type { PaymentAssignmentUserDto } from '../../api/dto/get-payment-assignment-users.dto'
import { usePaymentAssignmentUserOptions } from '../../components/PaymentAssignment/hooks/usePaymentAssignmentUserOptions'
import Scoreboard from '../../components/PaymentAssignment/Scoreboard'
import PaymentAssignmentTable from '../../components/PaymentAssignment/PaymentAssignmentTable'
import { usePaymentAssignment } from '../../components/PaymentAssignment/hooks/usePaymentAssignment'
import AssignReassignModal, {
  AssignModalType,
} from '../../components/PaymentAssignment/Modal/AssignReassignModal'
import PaymentDetailModal from '../../components/PaymentAssignment/PaymentDetailModal/PaymentDetailModal'
import BulkAssignmentModal from '../../components/PaymentAssignment/Modal/BulkAssignmentModal'
import BulkUnassignModal, {
  BulkUnassignType,
} from '../../components/PaymentAssignment/Modal/BulkUnassignModal'
import { CancelBotPaymentModal } from '../../components/PaymentAssignment/Modal/CancelBotPaymentModal'
import UpdateStatusModal from '../../components/PaymentAssignment/Modal/UpdateStatusModal'
import PaymentAssignmentFilter from '../../components/PaymentAssignment/PaymentAssignmentFilter'
import { AxiosError } from 'axios'

const { Content } = Layout

export default function PaymentAssignment() {
  const platformRole = useSelector(authSelectors.getRole)
  const userId = useSelector(authSelectors.getUserId)

  const isPlatformAdmin = platformRole === ROLE_PLATFORM_HIXME_ADMIN
  const userIdString = isNil(userId) ? '' : String(userId)
  const userIdNumber =
    !isNil(userId) && Number.isFinite(Number(userId))
      ? Number(userId)
      : undefined
  const {
    data: paymentAssignmentRoleData,
    isFetched: isRoleFetched,
    isLoading: isRoleLoading,
  } = usePaymentAssignmentUserRoleQuery()
  const backendPaymentAssignmentRole = paymentAssignmentRoleData?.role ?? '—'

  const hasResolvedPaymentAssignmentRole = !userIdString || isRoleFetched
  // Determine current "Payment Assignment role". While role lookup is pending,
  // keep UNKNOWN to avoid rendering the wrong scoreboard briefly.
  const currentPaymentAssignmentRole: PaymentAssignmentRole =
    paymentAssignmentRoleData?.role ??
    (hasResolvedPaymentAssignmentRole
      ? isPlatformAdmin
        ? PaymentAssignmentRole.CHECKER
        : PaymentAssignmentRole.UNKNOWN
      : PaymentAssignmentRole.UNKNOWN)
  const rolePolicy = usePaymentAssignmentRolePolicy(
    currentPaymentAssignmentRole
  )
  const isReadOnlyView =
    currentPaymentAssignmentRole === PaymentAssignmentRole.UNKNOWN

  const isCheckerOrViewer =
    currentPaymentAssignmentRole === PaymentAssignmentRole.CHECKER ||
    currentPaymentAssignmentRole === PaymentAssignmentRole.UNKNOWN

  const isChecker =
    currentPaymentAssignmentRole === PaymentAssignmentRole.CHECKER

  const handleAssignmentNav = useCallback(
    (target: 'metrics' | 'bulk-clear' | 'bulk-assign' | 'bulk-status') => {
      if (isCheckerOrViewer) {
        const pathMap: Record<typeof target, string> = {
          metrics: `${FINANCE_PATH}/payment-assignment/metrics`,
          'bulk-clear': `${FINANCE_PATH}/payment-assignment/bulk-clear`,
          'bulk-assign': `${FINANCE_PATH}/payment-assignment/bulk-assign`,
          'bulk-status': `${FINANCE_PATH}/payment-assignment/bulk-status`,
        }
        browserHistory.push(pathMap[target])
      } else {
        browserHistory.push(`${FINANCE_PATH}/payment-assignment`)
      }
    },
    [isCheckerOrViewer]
  )

  const { items: makersItems, isLoading: makersLoading } =
    usePaymentAssignmentUserOptions(PaymentAssignmentRole.MAKER, {
      enabled: rolePolicy.showMakerColumn && Boolean(userIdString),
    })
  const makersData = { items: makersItems }

  const { items: escalatorsItems, isLoading: escalatorsLoading } =
    usePaymentAssignmentUserOptions(PaymentAssignmentRole.ESCALATOR, {
      enabled: rolePolicy.showEscalatorColumn && Boolean(userIdString),
    })
  const escalatorsData = { items: escalatorsItems }
  const [isInitialFilterReady, setIsInitialFilterReady] = useState(false)
  const shouldApplyInitialFiltersRef = useRef(false)

  // When role changes (e.g. ESCALATOR → MAKER after refetch), re-run initial assignee logic
  useEffect(() => {
    setIsInitialFilterReady(false)
  }, [currentPaymentAssignmentRole])
  const normalizedUserId = useMemo(
    () => userIdString.trim().toLowerCase(),
    [userIdString]
  )
  // Auth userId is AWS Cognito ID (opaque string). Match by string equality with API user.userId/user.email.
  const matchesCurrentUser = useCallback(
    (user: PaymentAssignmentUserDto) => {
      // Coerce to string so we handle API returning userId/email as number; avoid .trim() on non-string
      const normalizedAssignmentUserId = String(user.userId ?? '')
        .trim()
        .toLowerCase()
      const normalizedAssignmentEmail = String(user.email ?? '')
        .trim()
        .toLowerCase()
      if (
        normalizedUserId &&
        (normalizedAssignmentUserId === normalizedUserId ||
          normalizedAssignmentEmail === normalizedUserId)
      ) {
        return true
      }
      if (!Number.isFinite(userIdNumber)) {
        return false
      }
      const assignmentUserIdAsNumber = Number(user.userId)
      return (
        Number.isFinite(assignmentUserIdAsNumber) &&
        assignmentUserIdAsNumber === userIdNumber
      )
    },
    [normalizedUserId, userIdNumber]
  )
  const isRoleResolutionReady = !normalizedUserId || isRoleFetched
  const currentMakerAssignmentId = useMemo(() => {
    if (currentPaymentAssignmentRole !== PaymentAssignmentRole.MAKER) {
      return undefined
    }
    const items = makersData?.items ?? []
    // Prefer the MAKER-role record first; fall back to any match (e.g. CHECKER record)
    const matchedMaker =
      items.find(
        (u) => matchesCurrentUser(u) && u.role === PaymentAssignmentRole.MAKER
      ) ?? items.find((u) => matchesCurrentUser(u))
    return matchedMaker?.id
  }, [currentPaymentAssignmentRole, makersData?.items, matchesCurrentUser])

  const currentEscalatorAssignmentId = useMemo(() => {
    if (currentPaymentAssignmentRole !== PaymentAssignmentRole.ESCALATOR) {
      return undefined
    }
    const items = escalatorsData?.items ?? []
    // Prefer the ESCALATOR-role record first; fall back to any match (e.g. CHECKER record)
    const matchedEscalator =
      items.find(
        (u) =>
          matchesCurrentUser(u) && u.role === PaymentAssignmentRole.ESCALATOR
      ) ?? items.find((u) => matchesCurrentUser(u))
    return matchedEscalator?.id
  }, [currentPaymentAssignmentRole, escalatorsData?.items, matchesCurrentUser])

  const {
    filteredCases,
    rawCasesCount,
    migratedCount,
    isPaymentAssignmentFetched,
    isPaymentAssignmentError,
    isPaymentAssignmentFetching,
    selectedRowKeys,
    pagination,
    sort,
    filters,
    updateFilter,
    clearAllFilters,
    hasPendingFilterChanges,
    refreshPaymentAssignments,
    refetchPaymentAssignments,
    handlePaginationChange,
    handleSortChange,
    handleRowSelectionChange,
    updateCaseStatus,
    bulkAssignCases,
    updateCaseEscalator,
    removeCaseById: _removeCaseById,
    detailCaseId,
    detailCase,
    detailLoading,
    openDetailModal,
    closeDetailModal,
  } = usePaymentAssignment({
    queryEnabled: isInitialFilterReady,
    role: currentPaymentAssignmentRole,
    lockedAssigneeId: currentMakerAssignmentId,
    lockedEscalatorId: currentEscalatorAssignmentId,
  })

  const taskSummaryParams = useMemo(() => {
    const now = new Date()
    const period = filters.paymentPeriod
    const testUserFilter = TestUserFilter.NON_TEST_ONLY

    const terminatedFilter = TerminatedFilter.ALL

    if (!period || typeof period !== 'string') {
      return {
        year: now.getFullYear(),
        month: now.getMonth(),
        testUserFilter,
        terminatedFilter,
      }
    }
    const [yearStr, monthStr] = period.split('-')
    const year = Number(yearStr) || now.getFullYear()
    const month1Based = Number(monthStr)
    const month =
      Number.isFinite(month1Based) && month1Based >= 1 && month1Based <= 12
        ? month1Based - 1
        : now.getMonth()
    return {
      year,
      month,
      testUserFilter,
      terminatedFilter,
    }
  }, [filters.paymentPeriod])

  const {
    data: taskSummary,
    isLoading: taskSummaryLoading,
    refetch: refetchTaskSummary,
  } = usePaymentAssignmentTaskSummaryQuery(taskSummaryParams)

  const handleRefresh = useCallback(() => {
    refreshPaymentAssignments()
    refetchTaskSummary()
  }, [refreshPaymentAssignments, refetchTaskSummary])

  const handleRefetch = useCallback(() => {
    refetchPaymentAssignments()
    refetchTaskSummary()
  }, [refetchPaymentAssignments, refetchTaskSummary])

  const canUpdateDetailCaseRef = useRef(false)
  const prevDetailCaseIdRef = useRef<number | null>(null)
  if (detailCaseId !== prevDetailCaseIdRef.current) {
    prevDetailCaseIdRef.current = detailCaseId
    canUpdateDetailCaseRef.current = false
  }
  if (detailCaseId !== null && !canUpdateDetailCaseRef.current) {
    const canUpdate =
      !isReadOnlyView &&
      (currentPaymentAssignmentRole !== PaymentAssignmentRole.MAKER ||
        (Number.isFinite(currentMakerAssignmentId) &&
          detailCase?.assigneeId === currentMakerAssignmentId))
    if (canUpdate) canUpdateDetailCaseRef.current = true
  }
  const canUpdateDetailCase = canUpdateDetailCaseRef.current

  const assignPaymentAssignmentsMutation = useAssignPaymentAssignmentsMutation()
  const createPaymentBotTaskMutation = useCreatePaymentBotTaskMutation()
  const bulkCreatePaymentBotTaskMutation = useBulkCreatePaymentBotTaskMutation()
  const cancelPaymentBotTaskMutation = useCancelPaymentBotTaskMutation()
  const bulkCancelPaymentBotTaskMutation = useBulkCancelPaymentBotTaskMutation()
  const [cancelBotModalCases, setCancelBotModalCases] = useState<PaymentCase[]>(
    []
  )

  useEffect(() => {
    if (isInitialFilterReady) return
    if (!isRoleResolutionReady) return
    // Wait for userId before deciding assignee; otherwise we'd never set "me" as assignee
    if (!normalizedUserId) return

    if (rolePolicy.showMakerColumn && makersLoading) return
    if (rolePolicy.showEscalatorColumn && escalatorsLoading) return

    let didChange = false

    // --- Permission-aware sanitization of URL-restored filters ---
    //
    // A shared URL may carry filter values that the current user's role either
    // cannot see or is not allowed to interact with. Sanitize before the first
    // query so the recipient always gets a legally-scoped view.

    // 1. Strip any statuses outside the role's allowed set.
    const allowedStatusSet = new Set<string>(rolePolicy.allowedStatuses)
    const currentStatuses = filters.statuses ?? []
    const sanitizedStatuses = currentStatuses.filter((s) =>
      allowedStatusSet.has(s)
    )
    if (sanitizedStatuses.length !== currentStatuses.length) {
      updateFilter('statuses', sanitizedStatuses)
      didChange = true
    }

    // 2. Drop filters whose drawer control isn't available for this role —
    //    they would be invisible, un-clearable ghost filters.
    if (
      !rolePolicy.canFilterByEscalator &&
      (filters.escalatorIds ?? []).length > 0
    ) {
      updateFilter('escalatorIds', [])
      didChange = true
    }
    if (
      !rolePolicy.canFilterByAssignee &&
      (filters.assigneeIds ?? []).length > 0
    ) {
      updateFilter('assigneeIds', [])
      didChange = true
    }

    // --- Role-based self-scoping (runs even when URL carried filters) ---
    //
    // MAKER: the assignee filter is ALWAYS locked to the current user — both
    // as the default on a fresh load and as a hard permission boundary when
    // opening a shared URL. A MAKER must only see their own assignments.
    //
    // If the current user cannot be resolved in the makers list (e.g. their
    // record hasn't propagated yet), clear the assignee filter so the query
    // does not run with whatever URL-provided IDs were present.
    if (currentPaymentAssignmentRole === PaymentAssignmentRole.MAKER) {
      const makerItems = makersData?.items ?? []
      const matchedMaker =
        makerItems.find(
          (u) => matchesCurrentUser(u) && u.role === PaymentAssignmentRole.MAKER
        ) ?? makerItems.find((u) => matchesCurrentUser(u))
      if (matchedMaker) {
        const currentAssigneeIds = filters.assigneeIds ?? []
        const isAlreadySelf =
          currentAssigneeIds.length === 1 &&
          currentAssigneeIds[0] === matchedMaker.id
        if (!isAlreadySelf) {
          updateFilter('assigneeIds', [matchedMaker.id])
          didChange = true
        }
      } else {
        // Current user not found in the makers list (data loaded, no match).
        // Clear any URL-supplied foreign ID on the first pass so no stale
        // assignee is sent, then keep the query permanently blocked by
        // returning without setting isInitialFilterReady — never let an
        // unscoped MAKER query through (empty assigneeIds = all cases in API).
        if ((filters.assigneeIds ?? []).length > 0) {
          updateFilter('assigneeIds', [])
        }
        return
      }
    }

    // ESCALATOR: similarly always scoped to self — an escalator only manages
    // their own cases and must not view another escalator's queue via a link.
    if (currentPaymentAssignmentRole === PaymentAssignmentRole.ESCALATOR) {
      const escalatorItems = escalatorsData?.items ?? []
      const matchedEscalator =
        escalatorItems.find(
          (u) =>
            matchesCurrentUser(u) && u.role === PaymentAssignmentRole.ESCALATOR
        ) ?? escalatorItems.find((u) => matchesCurrentUser(u))
      if (matchedEscalator) {
        const currentEscalatorIds = filters.escalatorIds ?? []
        const isAlreadySelf =
          currentEscalatorIds.length === 1 &&
          currentEscalatorIds[0] === matchedEscalator.id
        if (!isAlreadySelf) {
          updateFilter('escalatorIds', [matchedEscalator.id])
          didChange = true
        }
      } else {
        // Current user not found in the escalators list after data loaded.
        // Clear any URL-supplied foreign ID on the first pass, then keep the
        // query blocked — never let an unscoped ESCALATOR query through.
        if ((filters.escalatorIds ?? []).length > 0) {
          updateFilter('escalatorIds', [])
        }
        return
      }
    }

    if (didChange) {
      shouldApplyInitialFiltersRef.current = true
      return
    }

    setIsInitialFilterReady(true)
  }, [
    currentPaymentAssignmentRole,
    escalatorsData?.items,
    escalatorsLoading,
    filters.assigneeIds,
    filters.escalatorIds,
    filters.statuses,
    isInitialFilterReady,
    makersData?.items,
    makersLoading,
    matchesCurrentUser,
    normalizedUserId,
    isRoleResolutionReady,
    rolePolicy.allowedStatuses,
    rolePolicy.canFilterByAssignee,
    rolePolicy.canFilterByEscalator,
    updateFilter,
  ])

  useEffect(() => {
    if (!shouldApplyInitialFiltersRef.current) return
    if (!hasPendingFilterChanges) return

    handleRefresh()
    shouldApplyInitialFiltersRef.current = false
    setIsInitialFilterReady(true)
  }, [hasPendingFilterChanges, handleRefresh])

  // Auto-refetch when the payment period changes (mirrors Scoreboard behaviour).
  // Skip on initial mount — the first fetch is driven by the initial-filter-ready flow above.
  const prevPaymentPeriodRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (!isInitialFilterReady) return
    const current = filters.paymentPeriod
    if (prevPaymentPeriodRef.current === undefined) {
      prevPaymentPeriodRef.current = current
      return
    }
    if (current === prevPaymentPeriodRef.current) return
    prevPaymentPeriodRef.current = current
    handleRefresh()
  }, [filters.paymentPeriod, isInitialFilterReady, handleRefresh])

  const [bulkAssignModalVisible, setBulkAssignModalVisible] = useState(false)
  const [bulkUnassignModalVisible, setBulkUnassignModalVisible] =
    useState(false)
  const [bulkUnassignType, setBulkUnassignType] = useState<BulkUnassignType>(
    PaymentAssignmentRole.MAKER
  )
  const [bulkUnassignCases, setBulkUnassignCases] = useState<
    { id: number; benefitId: string }[]
  >([])
  const [assignReassignModalVisible, setAssignReassignModalVisible] =
    useState(false)
  const [assignModalType, setAssignModalType] =
    useState<AssignModalType>('assignee')
  const [assignCase, setAssignCase] = useState<PaymentCase | null>(null)
  const [assignReassignSubmitting, setAssignReassignSubmitting] =
    useState(false)
  const [updateStatusModalVisible, setUpdateStatusModalVisible] =
    useState(false)
  const [statusUpdateCase, setStatusUpdateCase] = useState<PaymentCase | null>(
    null
  )
  const [showUserDebugInfo, setShowUserDebugInfo] = useState(false)

  const handleRowClick = (paymentCase: PaymentCase) => {
    openDetailModal(paymentCase.id)
  }

  const handleMetricClick = (filterType: string) => {
    if (isReadOnlyView) return

    const isCheckerView =
      currentPaymentAssignmentRole === PaymentAssignmentRole.CHECKER
    const isMakerView =
      currentPaymentAssignmentRole === PaymentAssignmentRole.MAKER
    const isEscalatorView =
      currentPaymentAssignmentRole === PaymentAssignmentRole.ESCALATOR

    const makerScope = currentMakerAssignmentId
      ? [currentMakerAssignmentId]
      : []
    const escalatorScope = currentEscalatorAssignmentId
      ? [currentEscalatorAssignmentId]
      : []
    const allowed = rolePolicy.allowedStatuses as PaymentAssignmentStatusId[]

    let cardOverrides: Partial<typeof filters> | null = null

    if (isCheckerView) {
      switch (filterType) {
        case 'pendingAssignment':
          cardOverrides = {
            statuses: [PaymentAssignmentStatusId.PENDING_ASSIGNMENT],
          }
          break
        case 'escalatedCases':
          cardOverrides = {
            statuses: [
              PaymentAssignmentStatusId.UNPAID_ESCALATION,
              PaymentAssignmentStatusId.PAID_ESCALATION,
            ],
          }
          break
        case 'totalTodo':
          cardOverrides = {
            statuses: [
              PaymentAssignmentStatusId.PENDING_ASSIGNMENT,
              PaymentAssignmentStatusId.UNPAID_ESCALATION,
              PaymentAssignmentStatusId.PAID_ESCALATION,
            ],
          }
          break
        case 'totalCases':
          cardOverrides = { statuses: [] }
          break
      }
    } else if (isMakerView) {
      switch (filterType) {
        case 'remainingTasks':
          cardOverrides = {
            statuses: [
              PaymentAssignmentStatusId.PENDING_PAYMENT,
              PaymentAssignmentStatusId.REATTEMPT,
              PaymentAssignmentStatusId.BOT_FAILED,
            ],
            assigneeIds: makerScope,
          }
          break
        case 'completed':
          cardOverrides = {
            statuses: allowed.filter(
              (s) =>
                s !== PaymentAssignmentStatusId.PENDING_PAYMENT &&
                s !== PaymentAssignmentStatusId.REATTEMPT &&
                s !== PaymentAssignmentStatusId.BOT_FAILED
            ),
            assigneeIds: makerScope,
          }
          break
        case 'totalAssigned':
          cardOverrides = { statuses: [], assigneeIds: makerScope }
          break
      }
    } else if (isEscalatorView) {
      switch (filterType) {
        case 'pendingSolvingCase':
          cardOverrides = {
            statuses: [
              PaymentAssignmentStatusId.PAID_ESCALATION,
              PaymentAssignmentStatusId.UNPAID_ESCALATION,
            ],
            escalatorIds: escalatorScope,
          }
          break
        case 'totalEscalationDone':
          cardOverrides = {
            statuses: allowed.filter(
              (s) =>
                s !== PaymentAssignmentStatusId.PAID_ESCALATION &&
                s !== PaymentAssignmentStatusId.UNPAID_ESCALATION
            ),
            escalatorIds: escalatorScope,
          }
          break
        case 'totalCases':
          cardOverrides = { statuses: [], escalatorIds: escalatorScope }
          break
      }
    }

    if (!cardOverrides) return

    // Clean preset: purge every filter except paymentPeriod, then apply per-card overrides.
    // Guarantees the table reflects the same population the scoreboard counted.
    const cleanPreset: Partial<typeof filters> = {
      benefitEffectiveDateFrom: undefined,
      benefitEffectiveDateTo: undefined,
      benefitEndDateFrom: undefined,
      benefitEndDateTo: undefined,
      carrierNames: [],
      paymentTypes: [],
      priorityLevels: [],
      statuses: [],
      clientPublicKeys: [],
      assigneeIds: [],
      escalatorIds: [],
      paymentSubmittedByIds: [],
      search: undefined,
      testUserFilter: TestUserFilter.NON_TEST_ONLY,
      terminatedFilter: TerminatedFilter.ALL,
      relationships: [],
    }

    const updates: Partial<typeof filters> = {
      ...cleanPreset,
      ...cardOverrides,
    }
    for (const [key, value] of Object.entries(updates)) {
      updateFilter(
        key as keyof typeof filters,
        value as (typeof filters)[keyof typeof filters]
      )
    }
    refreshPaymentAssignments(updates)
    refetchTaskSummary()
  }

  const handleBulkAssign = (
    assigneeId: number | null,
    escalatorId: number | null,
    priority: Priority | null,
    notes: string
  ) => {
    if (!rolePolicy.canBulkAssign || !rolePolicy.canAssignMaker) {
      message.error('You do not have permission to bulk assign makers')
      return Promise.reject(new Error('Insufficient permission to bulk assign'))
    }

    if (!selectedRowKeys.length) {
      message.error('No cases selected')
      return Promise.reject(new Error('No cases selected'))
    }

    if (assigneeId === null && escalatorId === null && priority === null) {
      message.error(
        'Please select at least one of Maker, Escalator, or Priority'
      )
      return Promise.reject(
        new Error('At least one of Maker, Escalator, or Priority is required')
      )
    }

    const selectedCases = visibleCases.filter((c) =>
      selectedRowKeys.includes(c.benefitId)
    )
    const paymentAssignmentIds = selectedCases.map((c) => c.id)

    if (!paymentAssignmentIds.length) {
      message.error('Selected cases are not available on this page')
      return Promise.reject(
        new Error('Selected cases are not available on this page')
      )
    }

    return assignPaymentAssignmentsMutation
      .mutateAsync({
        paymentAssignmentIds,
        ...(assigneeId !== null ? { assigneeUserId: assigneeId } : {}),
        ...(escalatorId !== null ? { escalatorUserId: escalatorId } : {}),
        ...(priority !== null ? { priority } : {}),
        notes,
      })
      .then((res) => {
        // null from bulk modal means "not provided" — convert to undefined so
        // bulkAssignCases treats it as "no change" rather than "unassign"
        bulkAssignCases(
          selectedRowKeys,
          assigneeId ?? undefined,
          escalatorId ?? undefined,
          priority,
          notes
        )
        handleRowSelectionChange([])
        setBulkAssignModalVisible(false)
        Modal.success({
          title: 'Bulk assignment successful',
          content: `Bulk ${res.success} payment assignment(s) successful`,
          centered: true,
        })
      })
      .catch((e: any) => {
        const messageText =
          e?.response?.data?.message || e?.message || 'Assignment failed'
        Modal.error({
          title: 'Bulk assignment failed',
          content: messageText,
          centered: true,
        })
        throw e
      })
      .finally(() => {
        handleRefetch()
      })
  }

  const handleBulkUnassignMaker = () => {
    if (!rolePolicy.canBulkAssign || !rolePolicy.canAssignMaker) {
      message.error('You do not have permission to unassign makers')
      return
    }
    if (!selectedRowKeys.length) {
      message.error('No cases selected')
      return
    }
    const selectedCases = visibleCases.filter(
      (c) =>
        selectedRowKeys.includes(c.benefitId) &&
        !isNil(c.assigneeId) &&
        c.assigneeId !== 0
    )
    if (!selectedCases.length) {
      message.error('None of the selected cases have a maker assigned')
      return
    }
    setBulkUnassignCases(
      selectedCases.map((c) => ({ id: c.id, benefitId: c.benefitId }))
    )
    setBulkUnassignType(PaymentAssignmentRole.MAKER)
    setBulkUnassignModalVisible(true)
  }

  const handleBulkUnassignEscalator = () => {
    if (!rolePolicy.canBulkAssign || !rolePolicy.canAssignEscalator) {
      message.error('You do not have permission to unassign escalators')
      return
    }
    if (!selectedRowKeys.length) {
      message.error('No cases selected')
      return
    }
    const selectedCases = visibleCases.filter(
      (c) =>
        selectedRowKeys.includes(c.benefitId) &&
        !isNil(c.escalatorId) &&
        c.escalatorId !== 0
    )
    if (!selectedCases.length) {
      message.error('None of the selected cases have an escalator assigned')
      return
    }
    setBulkUnassignCases(
      selectedCases.map((c) => ({ id: c.id, benefitId: c.benefitId }))
    )
    setBulkUnassignType(PaymentAssignmentRole.ESCALATOR)
    setBulkUnassignModalVisible(true)
  }

  const handleBulkUnassignConfirm = async (notes: string) => {
    const paymentAssignmentIds = bulkUnassignCases.map((c) => c.id)
    const affectedBenefitIds = bulkUnassignCases.map((c) => c.benefitId)
    const isMaker = bulkUnassignType === PaymentAssignmentRole.MAKER

    return assignPaymentAssignmentsMutation
      .mutateAsync({
        paymentAssignmentIds,
        ...(isMaker ? { assigneeUserId: null } : { escalatorUserId: null }),
        notes,
      })
      .then((res) => {
        bulkAssignCases(
          affectedBenefitIds,
          isMaker ? null : undefined,
          isMaker ? undefined : null,
          null,
          notes
        )
        handleRowSelectionChange([])
        setBulkUnassignModalVisible(false)
        setBulkUnassignCases([])
        Modal.success({
          title: isMaker ? 'Maker unassigned' : 'Escalator unassigned',
          content: `${res.success} case(s) unassigned successfully`,
          centered: true,
        })
      })
      .catch((e: any) => {
        const messageText =
          e?.response?.data?.message ||
          e?.message ||
          (isMaker ? 'Unassign maker failed' : 'Unassign escalator failed')
        Modal.error({
          title: isMaker
            ? 'Unassign maker failed'
            : 'Unassign escalator failed',
          content: messageText,
          centered: true,
        })
        throw e
      })
      .finally(() => {
        handleRefetch()
      })
  }

  const handleAssigneeClick = (paymentCase: PaymentCase) => {
    if (!rolePolicy.canAssignMaker) {
      message.error('You do not have permission to assign makers')
      return
    }
    setAssignCase(paymentCase)
    setAssignModalType('assignee')
    setAssignReassignModalVisible(true)
  }

  const handleEscalatorClick = (paymentCase: PaymentCase) => {
    if (!rolePolicy.canAssignEscalator) {
      message.error('You do not have permission to assign escalators')
      return
    }
    setAssignCase(paymentCase)
    setAssignModalType('escalator')
    setAssignReassignModalVisible(true)
  }

  const handlePriorityClick = (paymentCase: PaymentCase) => {
    if (!rolePolicy.canAssignPriority) {
      message.error('You do not have permission to update priority')
      return
    }
    setAssignCase(paymentCase)
    setAssignModalType('priority')
    setAssignReassignModalVisible(true)
  }

  const confirmRunBotClick = (paymentCase: PaymentCase) => {
    if (!rolePolicy.canSubmitPayment) {
      message.error('You do not have permission to submit payments')
      return
    }
    Modal.confirm({
      title: 'Submit payment',
      content: 'Are you sure you want to submit the payment for this benefit?',
      okText: 'Submit',
      cancelText: 'Cancel',
      onOk: () => {
        return createPaymentBotTaskMutation
          .mutateAsync({
            paymentAssignmentId: paymentCase.id,
          })
          .then(() => {
            message.success('Payment bot task submitted successfully')
          })
          .catch((e: any) => {
            const errMessage =
              e?.response?.data?.message ||
              e?.message ||
              'Failed to submit payment bot task'
            message.error(errMessage)
            throw e
          })
      },
    })
  }

  const openCancelBotModal = (selectedCases: PaymentCase[]) => {
    if (!rolePolicy.canCancelPayment) {
      message.error('You do not have permission to cancel bot payments')
      return
    }
    setCancelBotModalCases(selectedCases)
  }

  const closeCancelBotModal = () => {
    setCancelBotModalCases([])
  }

  const handleConfirmCancelBot = async (reason: string) => {
    const casesToCancel = cancelBotModalCases
    if (casesToCancel.length === 0) {
      return
    }
    try {
      if (casesToCancel.length === 1) {
        const paymentCase = casesToCancel[0]
        const response = await cancelPaymentBotTaskMutation.mutateAsync({
          paymentAssignmentId: paymentCase.id,
          reason,
        })
        const assignmentId = response.assignmentId ?? paymentCase.id
        if (response.assignmentStatus != null) {
          updateCaseStatus(assignmentId, response.assignmentStatus)
        }
        message.success('Bot payment cancelled successfully')
      } else {
        const paymentAssignmentIds = casesToCancel.map((c) => c.id)
        const response = await bulkCancelPaymentBotTaskMutation.mutateAsync({
          paymentAssignmentIds,
          reason,
        })
        const failed = response.results.filter((r) => !r.success)
        const succeeded = response.results.filter((r) => r.success)
        if (succeeded.length > 0) {
          for (const result of succeeded) {
            if (result.assignmentStatus != null) {
              updateCaseStatus(
                result.paymentAssignmentId,
                result.assignmentStatus
              )
            }
          }
          message.success(
            `Cancelled bot payment for ${succeeded.length} benefit(s)`
          )
        }
        if (failed.length > 0) {
          if (failed.length === 1) {
            message.error(failed[0]?.error ?? 'Cancellation failed')
          } else {
            Modal.error({
              title: `${failed.length} cancellation(s) failed`,
              centered: true,
              content: (
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {failed.map((f) => (
                    <li key={f.paymentAssignmentId}>
                      Assignment {f.paymentAssignmentId}:{' '}
                      {f.error ?? 'Cancellation failed'}
                    </li>
                  ))}
                </ul>
              ),
            })
          }
          const failedAssignmentIds = failed.map((f) => f.paymentAssignmentId)
          const failedCases = casesToCancel.filter((c) =>
            failedAssignmentIds.includes(c.id)
          )
          const failedBenefitIds = failedCases.map((c) => c.benefitId)
          handleRowSelectionChange(failedBenefitIds)
          setCancelBotModalCases(failedCases)
          handleRefetch()
          if (detailCaseId != null) {
            const detailCaseSucceeded = succeeded.some(
              (s) => s.paymentAssignmentId === detailCaseId
            )
            if (detailCaseSucceeded) {
              closeDetailModal()
            }
          }
          return
        }
      }
      handleRowSelectionChange([])
      handleRefetch()
      closeCancelBotModal()
      if (detailCaseId != null) {
        const detailCaseWasCancelled = casesToCancel.some(
          (c) => c.id === detailCaseId
        )
        if (detailCaseWasCancelled) {
          closeDetailModal()
        }
      }
    } catch (e: unknown) {
      const axiosErr = e instanceof Error ? (e as AxiosError) : null
      message.error(
        axiosErr?.response?.data?.message ??
          axiosErr?.message ??
          'Failed to cancel bot payment'
      )
    }
  }

  const confirmBulkRunBotClick = (selectedCases: PaymentCase[]) => {
    if (!rolePolicy.canSubmitPayment) {
      message.error('You do not have permission to submit payments')
      return
    }
    Modal.confirm({
      title: 'Submit payment',
      content: `Are you sure you want to submit the payment for these ${selectedCases.length} benefits?`,
      okText: 'Submit',
      cancelText: 'Cancel',
      onOk: () => {
        const paymentAssignmentIds = selectedCases.map(
          (paymentCase) => paymentCase.id
        )

        return bulkCreatePaymentBotTaskMutation
          .mutateAsync({ paymentAssignmentIds })
          .then(() => {
            message.success('Payment bot tasks submitted successfully')
          })
          .catch((e: any) => {
            const errMessage =
              e?.response?.data?.message ||
              e?.message ||
              'Failed to submit payment bot tasks'
            message.error(errMessage)
            throw e
          })
      },
    })
  }

  const handleAssign = (
    caseId: number,
    assigneeId: number | null,
    notes: string
  ) => {
    if (!rolePolicy.canAssignMaker) {
      message.error('You do not have permission to assign makers')
      return Promise.reject(
        new Error('Insufficient permission to assign maker')
      )
    }
    setAssignReassignSubmitting(true)
    return assignPaymentAssignmentsMutation
      .mutateAsync({
        paymentAssignmentIds: [caseId],
        assigneeUserId: assigneeId,
        notes,
      })
      .then((_res) => {
        const isRemove = assigneeId === null
        const benefitId = assignCase?.benefitId
        Modal.success({
          title: isRemove ? 'Maker removed' : 'Assignment successful',
          content: (
            <div>
              <div>Payment Assignment ID: {caseId}</div>
              {benefitId && <div>Benefit ID: {benefitId}</div>}
              <div style={{ marginTop: 8 }}>
                {isRemove
                  ? 'Maker removed successfully'
                  : 'Assigned successfully'}
              </div>
            </div>
          ),
          centered: true,
        })
        setAssignReassignModalVisible(false)
        setAssignCase(null)
      })
      .catch((e: any) => {
        const isRemove = assigneeId === null
        const messageText =
          e?.response?.data?.message ||
          e?.message ||
          (isRemove ? 'Remove maker failed' : 'Assignment failed')
        Modal.error({
          title: isRemove ? 'Remove maker failed' : 'Assignment failed',
          content: messageText,
          centered: true,
        })
        throw e
      })
      .finally(() => {
        setAssignReassignSubmitting(false)
        handleRefetch()
      })
  }

  const handleAssignEscalator = (
    caseId: number,
    escalatorId: number | null,
    notes: string
  ) => {
    if (!rolePolicy.canAssignEscalator) {
      message.error('You do not have permission to assign escalators')
      return Promise.reject(
        new Error('Insufficient permission to assign escalator')
      )
    }
    setAssignReassignSubmitting(true)

    return assignPaymentAssignmentsMutation
      .mutateAsync({
        paymentAssignmentIds: [caseId],
        escalatorUserId: escalatorId,
        notes,
      })
      .then(() => {
        updateCaseEscalator(caseId, escalatorId)
        const isRemove = escalatorId === null
        const benefitId = assignCase?.benefitId
        Modal.success({
          title: isRemove ? 'Escalator removed' : 'Escalator assigned',
          content: (
            <div>
              <div>Payment Assignment ID: {caseId}</div>
              {benefitId && <div>Benefit ID: {benefitId}</div>}
              <div style={{ marginTop: 8 }}>
                {isRemove
                  ? 'Escalator removed successfully'
                  : 'Escalator assigned successfully'}
              </div>
            </div>
          ),
          centered: true,
        })
        setAssignReassignModalVisible(false)
        setAssignCase(null)
      })
      .catch((e: any) => {
        const isRemove = escalatorId === null
        const messageText =
          e?.response?.data?.message ||
          e?.message ||
          (isRemove ? 'Remove escalator failed' : 'Assign escalator failed')
        Modal.error({
          title: isRemove
            ? 'Remove escalator failed'
            : 'Assign escalator failed',
          content: messageText,
          centered: true,
        })
        throw e
      })
      .finally(() => {
        setAssignReassignSubmitting(false)
        handleRefetch()
      })
  }

  const handleAssignPriority = (
    caseId: number,
    priority: Priority,
    notes: string
  ) => {
    if (!rolePolicy.canAssignPriority) {
      message.error('You do not have permission to update priority')
      return Promise.reject(
        new Error('Insufficient permission to update priority')
      )
    }
    setAssignReassignSubmitting(true)
    return assignPaymentAssignmentsMutation
      .mutateAsync({
        paymentAssignmentIds: [caseId],
        priority,
        notes,
      })
      .then(() => {
        Modal.success({
          title: 'Priority updated',
          content: `Updated priority for payment assignment ${caseId} successfully`,
          centered: true,
        })
        setAssignReassignModalVisible(false)
        setAssignCase(null)
      })
      .catch((e: any) => {
        const messageText =
          e?.response?.data?.message || e?.message || 'Priority update failed'
        Modal.error({
          title: 'Priority update failed',
          content: messageText,
          centered: true,
        })
        throw e
      })
      .finally(() => {
        setAssignReassignSubmitting(false)
        handleRefetch()
      })
  }

  const handleStatusUpdate = (
    caseId: number,
    newStatus: string,
    note: string
  ) => {
    if (!rolePolicy.canUpdateStatus) {
      message.error('You do not have permission to update status')
      return
    }
    if (!rolePolicy.allowedStatuses.includes(newStatus as PaymentStatus)) {
      message.error('Selected status is not allowed for your role')
      return
    }
    updateCaseStatus(caseId, newStatus as PaymentStatus, note)
    message.success(`Status updated to ${newStatus}${note ? ' with note' : ''}`)
    setUpdateStatusModalVisible(false)
    setStatusUpdateCase(null)

    // Refetch to pick up backend auto-reassign (escalator taking ownership of another's case)
    if (currentPaymentAssignmentRole === PaymentAssignmentRole.ESCALATOR) {
      handleRefetch()
    }
  }

  // Table dataset must match backend pagination/filter results exactly.
  const visibleCases = filteredCases

  const tablePagination = {
    ...pagination,
    total: pagination.total,
  }

  const tableEmptyText = isPaymentAssignmentError ? (
    'Failed to load cases'
  ) : !isPaymentAssignmentFetched ? (
    <Spin />
  ) : rawCasesCount === 0 ? (
    'No cases loaded'
  ) : (
    'No results match filters'
  )

  return (
    <PageWrapper>
      <Page
        title="Payment Assignment"
        maxWidth="1600px"
        breadcrumb={
          <BreadcrumbNav
            items={[
              { title: 'Finance', path: FINANCE_PATH },
              { title: 'Payment Assignment' },
            ]}
          />
        }
        action={
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {!isRoleLoading && isCheckerOrViewer && (
              <>
                <Button
                  icon={<BarChartOutlined />}
                  onClick={() => handleAssignmentNav('metrics')}
                >
                  Metrics
                </Button>
              </>
            )}
            {!isRoleLoading && isChecker && (
              <>
                <Button
                  type="default"
                  icon={<UserAddOutlined />}
                  onClick={() => handleAssignmentNav('bulk-assign')}
                >
                  Bulk Assign
                </Button>
                <Button
                  type="default"
                  icon={<CheckSquareOutlined />}
                  onClick={() => handleAssignmentNav('bulk-status')}
                >
                  Bulk Update Status
                </Button>
              </>
            )}
          </div>
        }
      >
        <Layout style={{ backgroundColor: '#f6f9fc' }}>
          <Content style={{ padding: '24px', position: 'relative' }}>
            <div
              onClick={(e) => {
                if (e.detail === 3) {
                  setShowUserDebugInfo((prev) => !prev)
                }
              }}
              style={{
                position: 'absolute',
                top: 8,
                right: 8,
                width: 28,
                height: 28,
                cursor: 'pointer',
                zIndex: 2,
              }}
              title="Triple-click to toggle debug info"
            />
            {showUserDebugInfo && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  marginBottom: '12px',
                  color: '#677786',
                  fontSize: '13px',
                }}
              >
                User ID: {userIdString || '—'}
                <span>Role: {backendPaymentAssignmentRole}</span>
              </div>
            )}
            {!isRoleResolutionReady ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: 240,
                }}
              >
                <Spin />
              </div>
            ) : (
              <>
                {isReadOnlyView && (
                  <div
                    style={{
                      marginBottom: '12px',
                      padding: '10px 12px',
                      border: '1px solid #d6d8d9',
                      borderRadius: '4px',
                      background: '#fff',
                      color: '#677786',
                      fontSize: '13px',
                    }}
                  >
                    Read-only view: you can view payment assignments, but status
                    and assignment updates are disabled for your role.
                  </div>
                )}
                {/* Scoreboard */}
                <Scoreboard
                  cases={filteredCases}
                  totalCases={pagination.total}
                  onMetricClick={handleMetricClick}
                  currentUserId={userIdNumber}
                  role={currentPaymentAssignmentRole}
                  selectedAssigneeIds={filters.assigneeIds}
                  taskSummary={taskSummary ?? null}
                  taskSummaryLoading={taskSummaryLoading}
                />

                {/* Filters */}
                <PaymentAssignmentFilter
                  filters={filters}
                  updateFilter={updateFilter}
                  clearAllFilters={clearAllFilters}
                  currentPaymentAssignmentRole={currentPaymentAssignmentRole}
                  rolePolicy={rolePolicy}
                  onSearch={handleRefresh}
                  hasPendingChanges={hasPendingFilterChanges}
                  isSearching={isPaymentAssignmentFetching}
                  sort={sort}
                  migratedCount={migratedCount}
                />

                {/* Table */}
                <div style={{ minHeight: '100vh' }}>
                  <PaymentAssignmentTable
                    cases={visibleCases}
                    emptyText={tableEmptyText}
                    selectedRowKeys={selectedRowKeys}
                    pagination={tablePagination}
                    sort={sort}
                    onRowSelectionChange={handleRowSelectionChange}
                    onPaginationChange={handlePaginationChange}
                    onSortChange={handleSortChange}
                    onRowClick={handleRowClick}
                    onAssigneeClick={handleAssigneeClick}
                    onEscalatorClick={handleEscalatorClick}
                    onPriorityClick={handlePriorityClick}
                    onBulkAssignClick={() => setBulkAssignModalVisible(true)}
                    onBulkUnassignMakerClick={handleBulkUnassignMaker}
                    onBulkUnassignEscalatorClick={handleBulkUnassignEscalator}
                    onBulkRunBotClick={confirmBulkRunBotClick}
                    onRunBotClick={confirmRunBotClick}
                    onBulkCancelBotClick={openCancelBotModal}
                    onCancelBotClick={(paymentCase) =>
                      openCancelBotModal([paymentCase])
                    }
                    rolePolicy={rolePolicy}
                  />
                </div>

                {/* Modals */}
                <PaymentDetailModal
                  visible={detailCaseId !== null}
                  paymentCase={detailCase}
                  loading={detailLoading}
                  onClose={closeDetailModal}
                  onComplete={handleRefetch}
                  onStatusUpdated={(caseId, status) => {
                    updateCaseStatus(caseId, status)
                  }}
                  canUpdateCase={canUpdateDetailCase}
                  currentPaymentAssignmentRole={currentPaymentAssignmentRole}
                  rolePolicy={rolePolicy}
                />

                <BulkAssignmentModal
                  visible={bulkAssignModalVisible}
                  // `cases` is the current page only (from hook), but bulk selection can span pages.
                  // Use the full filtered dataset so the modal summary matches what will be assigned.
                  selectedCases={visibleCases.filter((c) =>
                    selectedRowKeys.includes(c.benefitId)
                  )}
                  onClose={() => setBulkAssignModalVisible(false)}
                  onAssign={handleBulkAssign}
                />

                <CancelBotPaymentModal
                  open={cancelBotModalCases.length > 0}
                  benefitCount={cancelBotModalCases.length}
                  loading={
                    cancelPaymentBotTaskMutation.isPending ||
                    bulkCancelPaymentBotTaskMutation.isPending
                  }
                  onClose={closeCancelBotModal}
                  onConfirm={handleConfirmCancelBot}
                />

                <AssignReassignModal
                  visible={assignReassignModalVisible}
                  paymentCase={assignCase}
                  assignType={assignModalType}
                  onClose={() => {
                    setAssignReassignModalVisible(false)
                    setAssignCase(null)
                    setAssignModalType('assignee')
                  }}
                  onAssign={handleAssign}
                  onAssignEscalator={handleAssignEscalator}
                  onAssignPriority={handleAssignPriority}
                  submitting={assignReassignSubmitting}
                />

                <BulkUnassignModal
                  visible={bulkUnassignModalVisible}
                  unassignType={bulkUnassignType}
                  caseCount={bulkUnassignCases.length}
                  onClose={() => {
                    setBulkUnassignModalVisible(false)
                    setBulkUnassignCases([])
                  }}
                  onConfirm={handleBulkUnassignConfirm}
                />

                <UpdateStatusModal
                  visible={updateStatusModalVisible}
                  paymentCase={statusUpdateCase}
                  onClose={() => {
                    setUpdateStatusModalVisible(false)
                    setStatusUpdateCase(null)
                  }}
                  onStatusUpdate={handleStatusUpdate}
                  currentPaymentAssignmentRole={currentPaymentAssignmentRole}
                  rolePolicy={rolePolicy}
                />
              </>
            )}
          </Content>
        </Layout>
      </Page>
    </PageWrapper>
  )
}
